import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SateLiteViewIcon from '../../../../assets/pipemap/satellite-view-btn.svg';
import RoadMapIcon from '../../../../assets/pipemap/terrain-view.png';
import PipeCluster from './PipeCluster';

import AlertBar from '../../../../components/AlertBar';
import { createMapControlIcon } from '../../helper';
import {
  COUNTRY_LOCATIONS,
  LIBRARIES,
  MAP_API_KEY,
  MAP_CONTAINER_STYLE,
  MAP_TYPES,
} from './config';

const Map = ({
  country,
  markers,
  error,
  selectedMarker,
  onMarkerClick,
  isFetching,
  currentPage,
  totalPages,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAP_API_KEY,
    libraries: LIBRARIES,
  });

  const [mapType, setMapType] = useState(MAP_TYPES.SATELLITE);
  const [zoom, setZoom] = useState(6);
  const mapRef = useRef();
  const mapBoundSetRef = useRef(-1);

  const options = useMemo(() => {
    return {
      streetViewControl: false,
      maxZoom: 21,
      mapTypeControl: false,
      styles: [
        {
          featureType: 'all',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }], // Hide all labels
        },
      ],
      mapTypeId: mapType,
    };
  }, [mapType]);

  const navigateToUserLocation = useCallback((map) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const pos = {
          lat: position?.coords?.latitude,
          lng: position?.coords?.longitude,
        };
        map.setCenter(pos);
      });
    }
  }, []);

  const toggleMapType = () => {
    setMapType((t) => {
      if (mapRef?.current) {
        const control = document.querySelector('.map-view-control');
        // To change the map type icon
        control.firstChild.firstChild.src =
          t === MAP_TYPES.SATELLITE ? SateLiteViewIcon : RoadMapIcon;
      }

      return t === MAP_TYPES.SATELLITE
        ? MAP_TYPES.TERRAIN
        : MAP_TYPES.SATELLITE;
    });
  };

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;

    // This is used to create a custom control for the map

    // Locate me custom control - to be added later
    // const locateControlDiv = document.createElement('div');
    // locateControlDiv.classList.add('locate-control');

    // createMapControlIcon(locateControlDiv, () => navigateToUserLocation(map), LocateMeIcon);

    // Change map view custom control

    const mapViewControlDiv = document.createElement('div');
    mapViewControlDiv.classList.add('map-view-control');
    createMapControlIcon(mapViewControlDiv, toggleMapType, RoadMapIcon);

    // Pushing the controls to the map
    // map.controls[window.google.maps.ControlPosition.BOTTOM_RIGHT].push(locateControlDiv);
    map.controls[window.google.maps.ControlPosition.BOTTOM_RIGHT].push(
      mapViewControlDiv,
    );
  }, []);

  const handleZoomChanged = useCallback(() => {
    if (mapRef?.current) {
      setZoom(mapRef?.current?.zoom);
    }
  }, []);

  useEffect(() => {
    if (country && !markers?.length) {
      mapRef?.current?.setCenter(COUNTRY_LOCATIONS[country]);
      mapRef?.current?.setZoom(6);
    }
  }, [country, markers?.length]);

  useEffect(() => {
    // This is used to set the map bounds to fit the markers

    if (currentPage !== 1 && currentPage !== totalPages)
      return clearTimeout(mapBoundSetRef.current);

    if (currentPage === 1 || currentPage === totalPages) {
      mapBoundSetRef.current = setTimeout(() => {
        if (mapRef.current && isLoaded && markers?.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((marker) => {
            bounds.extend(
              new window.google.maps.LatLng(marker?.lat, marker?.lng),
            );
          });
          mapRef.current.fitBounds(bounds);
        }
      }, 800);
    }

    return () => clearTimeout(mapBoundSetRef.current);
  }, [markers, isLoaded, currentPage, totalPages]);

  if (loadError) return 'Error loading maps';

  if (!isLoaded) return 'Loading maps';

  if (isLoaded)
    return (
      <>
        {error && <AlertBar message={error} type="error" />}
        <div className="map-container">
          <GoogleMap
            zoom={zoom}
            mapContainerStyle={MAP_CONTAINER_STYLE}
            options={options}
            onLoad={handleMapLoad}
            onZoomChanged={handleZoomChanged}
          >
            <PipeCluster
              zoom={zoom}
              pipeData={markers}
              onMarkerClick={onMarkerClick}
            />
          </GoogleMap>
          {/* <Drawer
            open={!!selectedMarker}
            title={selectedMarker?.label}
            content={<PipeDrawerContent pipe={selectedMarker} />}
            onClose={() => onMarkerClick(null)}
          /> */}
        </div>
      </>
    );
};

export default Map;
